import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { minorToMajor } from '@/lib/quoteMoney';

const API_URL = process.env.REACT_APP_API_URL;

const OPTIONAL_FAMILIES = [
  {
    familyId: 'security_cctv:altyapi:rack_guc:duvar_tipi_kabinet',
    labelTr: 'Kabinet',
    labelEn: 'Cabinet',
    defaultName: '4U',
  },
  {
    familyId: 'security_cctv:altyapi:rack_guc:ups',
    labelTr: 'UPS',
    labelEn: 'UPS',
    defaultName: '1000 VA',
  },
  {
    familyId: 'security_cctv:cctv:goruntuleme:guvenlik_monitoru',
    labelTr: 'İzleme Monitörü',
    labelEn: 'Security Monitor',
    defaultName: '24"',
  },
];

export function SecurityOptionalMasterItems({
  sectorKey,
  existingItems = [],
  disabled,
  language = 'tr',
  onAdd,
}) {
  const { t } = useTranslation('quotes');
  const [families, setFamilies] = useState({});
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (sectorKey !== 'security_cctv') {
      setFamilies({});
      setSelected({});
      return;
    }
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        OPTIONAL_FAMILIES.map(async (cfg) => {
          try {
            const { data } = await axios.get(
              `${API_URL}/api/quote/master-catalog/families/${encodeURIComponent(cfg.familyId)}`,
              {
                params: { sector_key: sectorKey, locale: language },
                withCredentials: true,
              },
            );
            return [cfg, data];
          } catch {
            return [cfg, null];
          }
        }),
      );
      if (cancelled) return;
      const nextFamilies = {};
      const nextSelected = {};
      results.forEach(([cfg, data]) => {
        if (!data) return;
        nextFamilies[cfg.familyId] = data;
        const variants = data.variants || [];
        const recommended = variants.find((v) => v.name === cfg.defaultName) || variants[0];
        if (recommended) nextSelected[cfg.familyId] = recommended.variant_id;
      });
      setFamilies(nextFamilies);
      setSelected(nextSelected);
    })();
    return () => { cancelled = true; };
  }, [sectorKey, language]);

  const existingFamilies = useMemo(
    () => new Set(existingItems.map((item) => item.master_family_id).filter(Boolean)),
    [existingItems],
  );

  if (sectorKey !== 'security_cctv') return null;

  const visible = OPTIONAL_FAMILIES.filter(
    (cfg) => families[cfg.familyId] && !existingFamilies.has(cfg.familyId),
  );
  if (visible.length === 0) return null;

  const add = (cfg) => {
    const family = families[cfg.familyId];
    const variant = (family.variants || []).find(
      (row) => row.variant_id === selected[cfg.familyId],
    );
    if (!variant) return;
    const price = Number(variant.unit_price_minor || 0);
    const cost = Number(variant.cost_price_minor || 0);
    onAdd({
      master_family_id: family.family_id,
      master_variant_id: variant.variant_id,
      master_catalog_revision: family.catalog_revision,
      technical_attributes: variant.technical_attributes || {},
      item_type: family.item_type || 'product',
      name: `${family.display_name || family.name} — ${variant.display_name || variant.name}`,
      description: [
        family.display_system_name || family.system_name,
        family.display_subsystem_name || family.subsystem_name,
      ].filter(Boolean).join(' / '),
      unit: family.display_unit || family.default_unit || 'adet',
      unit_price_major: price > 0 ? minorToMajor(price).toString() : '',
      cost_price_major: cost > 0 ? minorToMajor(cost).toString() : '',
      price_unset: price <= 0,
    });
  };

  return (
    <div className="mt-5 pt-4 border-t border-[#F1F5F9]" data-testid="quote-security-optional-items">
      <p className="text-sm font-semibold text-[#1A1A2E]">
        {t('form.optionalSecurity.title')}
      </p>
      <p className="text-xs text-[#64748B] mt-1">
        {t('form.optionalSecurity.hint')}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
        {visible.map((cfg) => {
          const family = families[cfg.familyId];
          return (
            <div key={cfg.familyId} className="rounded-xl border border-[#E5E5E5] p-3 bg-white">
              <p className="text-xs font-medium text-[#64748B] mb-2">
                {family.display_name || (language === 'tr' ? cfg.labelTr : cfg.labelEn)}
              </p>
              <div className="flex gap-2">
                <Select
                  value={selected[cfg.familyId] || ''}
                  onValueChange={(value) => setSelected((current) => ({
                    ...current,
                    [cfg.familyId]: value,
                  }))}
                  disabled={disabled}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder={t('form.optionalSecurity.choose')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(family.variants || []).map((variant) => (
                      <SelectItem key={variant.variant_id} value={variant.variant_id}>
                        {variant.display_name || variant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={disabled || !selected[cfg.familyId]}
                  onClick={() => add(cfg)}
                  aria-label={t('form.optionalSecurity.addAria')}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
